import { useCallback, useEffect, useRef, useState } from 'react';
import { SettingValue } from '../types/settingsTypes';

/**
 * Options for the useDraftCommit hook.
 *
 * @template TDraft - The type of the local draft buffer (what the input displays).
 * @template TValue - The type of the committed/prop value (what the store holds).
 */
export interface UseDraftCommitOptions<TDraft = SettingValue, TValue = SettingValue> {
    /** Committed value from prop/store. */
    value: TValue;
    /** Persist callback. May return void or Promise<void>. */
    onChange: (value: TValue) => void | Promise<void>;
    /**
     * Optional: transform draft to value form before persisting.
     * Defaults to identity cast (TDraft as unknown as TValue).
     */
    validate?: (draft: TDraft) => TValue;
    /**
     * Optional: convert prop value to draft form on sync-from-prop and revert.
     * Defaults to identity cast (TValue as unknown as TDraft).
     */
    serialize?: (value: TValue) => TDraft;
}

/**
 * Result returned by useDraftCommit.
 *
 * @template TDraft - The type of the local draft buffer.
 */
export interface DraftCommitResult<TDraft = SettingValue> {
    /** Local buffer - what the input displays. */
    draft: TDraft;
    /** Update buffer and mark dirty (for keystroke handlers). */
    setDraft: (v: TDraft) => void;
    /**
     * Validate and persist.
     * - No argument: reads current draft state. No-op if not dirty.
     * - Explicit argument: bypasses draft, dedup-checks via lastCommittedRef.
     */
    commit: (explicit?: TDraft) => void;
    /** Discard draft, reset to committed value. */
    revert: () => void;
    /** True if user has edited since last commit/revert/sync. */
    isDirty: boolean;
}

const COMMIT_PENDING_TIMEOUT_MS = 5000;

/**
 * Deferred-commit hook for settings inputs.
 *
 * Manages a local draft buffer that the input displays. Persists only on
 * explicit commit (blur, Enter, +/- buttons, slider mouseUp). External prop
 * changes (Reset, other tab) always overwrite the draft unless the user is
 * actively editing.
 */
export function useDraftCommit<TDraft = SettingValue, TValue = SettingValue>(
    options: UseDraftCommitOptions<TDraft, TValue>
): DraftCommitResult<TDraft> {
    const { value, onChange, validate, serialize } = options;

    const serializeFn = useCallback(
        (v: TValue): TDraft =>
            serialize ? serialize(v) : (v as unknown as TDraft),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [serialize]
    );
    const validateFn = useCallback(
        (d: TDraft): TValue =>
            validate ? validate(d) : (d as unknown as TValue),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [validate]
    );

    const [draft, setDraftState] = useState<TDraft>(() => serializeFn(value));
    const [isDirty, setIsDirty] = useState(false);

    const isDirtyRef = useRef(false);
    const commitPendingRef = useRef(false);
    const lastCommittedRef = useRef<TValue>(value);
    const prevValueRef = useRef<TValue>(value);
    const valueRef = useRef<TValue>(value);
    valueRef.current = value;
    const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const yieldTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            clearTimeout(pendingTimeoutRef.current);
            clearTimeout(yieldTimeoutRef.current);
        };
    }, []);

    const setDraft = useCallback((v: TDraft) => {
        setDraftState(v);
        isDirtyRef.current = true;
        setIsDirty(true);
    }, []);

    const revert = useCallback(() => {
        setDraftState(serializeFn(value));
        isDirtyRef.current = false;
        setIsDirty(false);
    }, [serializeFn, value]);

    // Ref avoids draft in commit()'s dep array, which would recreate it on every keystroke.
    const draftRef = useRef<TDraft>(draft);
    useEffect(() => {
        draftRef.current = draft;
    });

    const persistCommit = useCallback(
        async (committed: TValue) => {
            commitPendingRef.current = true;

            // Safety net: backend may return 200 OK without applying the change (non-editable settings).
            clearTimeout(pendingTimeoutRef.current);
            const timeoutId = setTimeout(() => {
                if (!mountedRef.current) return;
                commitPendingRef.current = false;
                if (!isDirtyRef.current) {
                    setDraftState(serializeFn(valueRef.current));
                }
            }, COMMIT_PENDING_TIMEOUT_MS);
            pendingTimeoutRef.current = timeoutId;

            try {
                await onChange(committed);
                // Yield one tick for React to flush store update into props before checking.
                clearTimeout(yieldTimeoutRef.current);
                yieldTimeoutRef.current = setTimeout(() => {
                    if (!mountedRef.current) return;
                    if (commitPendingRef.current && !isDirtyRef.current) {
                        clearTimeout(pendingTimeoutRef.current);
                        commitPendingRef.current = false;
                        setDraftState(serializeFn(valueRef.current));
                    }
                }, 0);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('useDraftCommit: onChange rejected:', error);
                if (!mountedRef.current) return;
                clearTimeout(pendingTimeoutRef.current);
                commitPendingRef.current = false;
                setDraftState(serializeFn(valueRef.current));
                isDirtyRef.current = false;
                setIsDirty(false);
            }
        },
        [onChange, serializeFn]
    );

    const commit = useCallback(
        (explicit?: TDraft) => {
            if (explicit !== undefined) {
                const committed = validateFn(explicit);
                // Dedup after validation so both sides are TValue (avoids cross-type === mismatches).
                if (committed === lastCommittedRef.current) {
                    return;
                }
                lastCommittedRef.current = committed;
                setDraftState(serializeFn(committed));
                isDirtyRef.current = false;
                setIsDirty(false);
                persistCommit(committed);
                return;
            }

            if (!isDirtyRef.current) {
                return;
            }

            const currentDraft = draftRef.current;
            const committed = validateFn(currentDraft);
            lastCommittedRef.current = committed;
            setDraftState(serializeFn(committed));
            isDirtyRef.current = false;
            setIsDirty(false);
            persistCommit(committed);
        },
        [validateFn, serializeFn, persistCommit]
    );

    useEffect(() => {
        if (prevValueRef.current === value) {
            return;
        }

        prevValueRef.current = value;
        lastCommittedRef.current = value;
        clearTimeout(pendingTimeoutRef.current);

        if (commitPendingRef.current) {
            // Own commit landed - only sync display if user hasn't started a new edit.
            commitPendingRef.current = false;
            if (!isDirtyRef.current) {
                setDraftState(serializeFn(value));
                isDirtyRef.current = false;
                setIsDirty(false);
            }
        } else {
            // External change (Reset, other tab, server push) always wins.
            setDraftState(serializeFn(value));
            isDirtyRef.current = false;
            setIsDirty(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return { draft, setDraft, commit, revert, isDirty };
}
